import { useState, useRef, useEffect, useContext } from "react";
import { Modal, Box, Tabs, Tab, AppBar } from "@material-ui/core";
import { useStyles } from "../Styles/styles";
import { withStyles } from "@material-ui/core/styles";
import PropTypes from "prop-types";
import PaginatedTableWrapper from "./PaginatedTableWrapper";
import { UserContext } from "../Contexts/UserContext";
import {
  parsePersonIncidentHistory,
  personIncidentHistoryHeaders,
  personDrugHistoryHeaders,
  parsePersonDrugHistory,
  personVehicleHistoryHeaders,
  parsePersonVehicleHistory,
  personNameHistoryHeaders,
  parsePersonNameHistory,
  personPhoneHistoryHeaders,
  parsePersonPhoneHistory,
  personAddressHistoryHeaders,
  parsePersonAddressHistory,
  knownAssociatesHeaders,
  parseKnownAssociates,
  aliasHistoryHeaders,
  parsePersonAliasHistory,
  personTicketHistoryHeaders,
  parsePersonTicketHistory,
} from "../utils/PersonHistoryHelper";
import { useLazyQuery, useMutation } from "@apollo/client";
import { PERSON_INCIDENT_HISTORY } from "../Queries/PersonIncidentHistory";
import { PERSON_DRUG_HISTORY } from "../Queries/PersonDrugHistory";
import { PERSON_VEHICLE_HISTORY } from "../Queries/PersonVehicleHistory";
import { PERSON_NAME_HISTORY } from "../Queries/PersonNameHistory";
import { PERSON_PHONE_HISTORY } from "../Queries/PersonPhoneHistory";
import { PERSON_ADDRESS_HISTORY } from "../Queries/PersonAddressHistory";
import { KNOWN_ASSOCIATES } from "../Queries/KnownAssociates";
import { ADD_OR_UPDATE_PERSON_IN_INCIDENT } from "../Mutations/AddOrUpdatePersonInIncident";
import { PERSON_ALIASES_QUERY } from "../Queries/PersonAliasesQuery";
import { PERSON_TICKET_HISTORY } from "../Queries/PersonTicketHistory";

function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box p={0}>{children}</Box>}
    </div>
  );
}

TabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.any.isRequired,
  value: PropTypes.any.isRequired,
};

function a11yProps(index) {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
}

const AntTabs = withStyles({
  root: {
    borderBottom: "none",
  },
  indicator: {
    display: "none",
  },
})(Tabs);

const AntTab = withStyles(theme => ({
  root: {
    backgroundColor: "lightgrey",
    "&:hover": {
      color: "#d9833b",
      opacity: 1,
    },
    "&$selected": {
      color: "white",
      backgroundColor: "rgb(48, 62, 79)",
      fontWeight: theme.typography.fontWeightMedium,
      border: "none",
      "& > span > div > svg": {
        color: "white",
        "&:hover": {
          color: "#d9833b !important",
        },
      },
    },
    marginRight: "5px",
  },
  selected: {},
}))(props => <Tab disableRipple {...props} />);

const PersonHistoryModal = ({
  modalOpen,
  setModalOpen,
  person,
  handlePersonOpening,
  handleIncidentOpening,
  handleTicketOpening,
  incidentId,
  handleAddPersonMatchToIncidentState,
  knownAssociatesNumber,
}) => {
  const classes = useStyles();
  const [value, setValue] = useState(0);
  const { user } = useContext(UserContext);
  const tabWrapperRef = useRef(null);

  const [dashSize, setDashSize] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
  });

  useEffect(() => {
    if (modalOpen && tabWrapperRef?.current?.offsetHeight) {
      setDashSize({
        width: tabWrapperRef.current.offsetWidth,
        height: tabWrapperRef.current.offsetHeight,
      });
    }
  }, [modalOpen]);

  useEffect(() => {
    function handleResize() {
      tabWrapperRef.current &&
        setDashSize({
          width: tabWrapperRef.current.offsetWidth,
          height: tabWrapperRef.current.offsetHeight,
        });
    }
    window.addEventListener("resize", handleResize);
    handleResize();
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const [addPersonToIncident] = useMutation(ADD_OR_UPDATE_PERSON_IN_INCIDENT, {
    onCompleted: data => {
      if (data) {
        !!handleAddPersonMatchToIncidentState &&
          handleAddPersonMatchToIncidentState(
            data.addOrUpdatePersonInIncident.incidentUuid,
            data.addOrUpdatePersonInIncident.personIncidentSpecific
          );
        setModalOpen(false);
      }
    },
  });

  const handleAddPersonToIncident = (person, incidentId) => {
    addPersonToIncident({
      variables: {
        input: {
          originatorId: user?.id,
          incidentId: incidentId,
          personId: person.id,
        },
      },
      context: {
        serializationKey: "incidentQueue",
      },
    });
  };

  const handleTabChange = newValue => {
    setValue(newValue);
  };

  // History fetching logic

  const knownAssociatesHeader = () => {
    let message = "Known Associates";
    if (knownAssociatesNumber > 0) {
      message = message + ` (${knownAssociatesNumber})`;
    }
    return message;
  };

  useEffect(() => {
    if (!modalOpen || !person?.id) return;

    const variables = { personId: person.id };
    switch (value) {
      case 0:
        personIncidentHistory({ variables });
        break;
      case 1:
        personDrugHistory({ variables });
        break;
      case 2:
        personVehicleHistory({ variables });
        break;
      case 3:
        personNameHistory({ variables });
        break;
      case 4:
        personPhoneHistory({ variables });
        break;
      case 5:
        personAddressHistory({ variables });
        break;
      case 6:
        aliasHistory({ variables });
        break;
      case 7:
        knownAssociates({ variables });
        break;
      case 8:
        personTicketHistory({ variables });
        break;

      default:
        break;
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modalOpen, person?.id, value]);

  const [
    personIncidentHistory,
    {
      loading: incidentHistoryLoading,
      data: incidentHistoryData,
      error: incidentHistoryError,
    },
  ] = useLazyQuery(PERSON_INCIDENT_HISTORY, {
    variables: { originatorId: user?.id },
    context: { serializationKey: "incidentQueue" },
    fetchPolicy: "network-only",
  });
  const [
    personDrugHistory,
    { loading: drugHistoryLoading, data: drugHistoryData },
  ] = useLazyQuery(PERSON_DRUG_HISTORY, {
    variables: { originatorId: user?.id },
    context: { serializationKey: "incidentQueue" },
    fetchPolicy: "network-only",
  });
  const [
    personVehicleHistory,
    { loading: vehicleHistoryLoading, data: vehicleHistoryData },
  ] = useLazyQuery(PERSON_VEHICLE_HISTORY, {
    variables: { originatorId: user?.id },
    context: { serializationKey: "incidentQueue" },
    fetchPolicy: "network-only",
  });
  const [
    personNameHistory,
    { loading: nameHistoryLoading, data: nameHistoryData },
  ] = useLazyQuery(PERSON_NAME_HISTORY, {
    variables: { originatorId: user?.id },
    context: { serializationKey: "incidentQueue" },
    fetchPolicy: "network-only",
  });
  const [
    personPhoneHistory,
    { loading: phoneHistoryLoading, data: phoneHistoryData },
  ] = useLazyQuery(PERSON_PHONE_HISTORY, {
    variables: { originatorId: user?.id },
    context: { serializationKey: "incidentQueue" },
    fetchPolicy: "network-only",
  });
  const [
    personAddressHistory,
    { loading: addressHistoryLoading, data: addressHistoryData },
  ] = useLazyQuery(PERSON_ADDRESS_HISTORY, {
    variables: { originatorId: user?.id },
    context: { serializationKey: "incidentQueue" },
    fetchPolicy: "network-only",
  });
  const [
    knownAssociates,
    {
      loading: knownAssociatesLoading,
      data: knownAssociatesData,
      error: knownAssociatesError,
    },
  ] = useLazyQuery(KNOWN_ASSOCIATES, {
    variables: { originatorId: user?.id },
    context: { serializationKey: "incidentQueue" },
    fetchPolicy: "network-only",
  });
  const [
    aliasHistory,
    {
      loading: aliasHistoryLoading,
      data: aliasHistoryData,
      error: aliasHistoryError,
    },
  ] = useLazyQuery(PERSON_ALIASES_QUERY, {
    variables: { originatorId: user?.id },
    context: { serializationKey: "incidentQueue" },
    fetchPolicy: "network-only",
  });
  const [
    personTicketHistory,
    {
      loading: ticketHistoryLoading,
      data: ticketHistoryData,
      error: ticketHistoryError,
    },
  ] = useLazyQuery(PERSON_TICKET_HISTORY, {
    variables: { originatorId: user?.id },
    context: { serializationKey: "incidentQueue" },
    fetchPolicy: "network-only",
  });

  return (
    <Modal
      // disableEnforceFocus
      open={modalOpen}
      onClose={() => setModalOpen(false)}
      className={classes.modal}
      data-cy="address-history-modal"
      keepMounted={false}
    >
      <div
        id={"tabs-wrapper"}
        style={{ height: "75vh", maxWidth: "75vw" }}
        className={classes.tabRoot}
        ref={tabWrapperRef}
      >
        <AppBar position="static" color="default">
          <AntTabs
            variant="scrollable"
            scrollButtons="auto"
            value={value}
            onChange={(e, newValue) => handleTabChange(newValue)}
            aria-label="simple tabs example"
          >
            <AntTab
              data-cy={"Person-incident-history-tab"}
              id={"Person-incident-history-tab"}
              style={{
                borderTopLeftRadius: "10px",
                borderTopRightRadius: "10px",
                border: "1px solid rgb(48, 62, 79)",
                borderBottom: "none",
              }}
              label={
                <div
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                  }}
                >
                  Incident History
                </div>
              }
              {...a11yProps(0)}
            />
            <AntTab
              data-cy={"Person-drug-history-tab"}
              id={"Person-drug-history-tab"}
              style={{
                borderTopLeftRadius: "10px",
                borderTopRightRadius: "10px",
                border: "1px solid rgb(48, 62, 79)",
                borderBottom: "none",
              }}
              label={
                <div
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                  }}
                >
                  Drug History
                </div>
              }
              {...a11yProps(1)}
            />
            <AntTab
              data-cy={"Person-vehicle-history-tab"}
              id={"Person-vehicle-history-tab"}
              style={{
                borderTopLeftRadius: "10px",
                borderTopRightRadius: "10px",
                border: "1px solid rgb(48, 62, 79)",
                borderBottom: "none",
              }}
              label={
                <div
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                  }}
                >
                  Vehicle History
                </div>
              }
              {...a11yProps(2)}
            />
            <AntTab
              data-cy={"Person-name-history-tab"}
              id={"Person-name-history-tab"}
              style={{
                borderTopLeftRadius: "10px",
                borderTopRightRadius: "10px",
                border: "1px solid rgb(48, 62, 79)",
                borderBottom: "none",
              }}
              label={
                <div
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                  }}
                >
                  Name History
                </div>
              }
              {...a11yProps(3)}
            />
            <AntTab
              data-cy={"Person-phone-history-tab"}
              id={"Person-phone-history-tab"}
              style={{
                borderTopLeftRadius: "10px",
                borderTopRightRadius: "10px",
                border: "1px solid rgb(48, 62, 79)",
                borderBottom: "none",
              }}
              label={
                <div
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                  }}
                >
                  Phone History
                </div>
              }
              {...a11yProps(4)}
            />
            <AntTab
              data-cy={"Person-address-history-tab"}
              id={"Person-address-history-tab"}
              style={{
                borderTopLeftRadius: "10px",
                borderTopRightRadius: "10px",
                border: "1px solid rgb(48, 62, 79)",
                borderBottom: "none",
              }}
              label={
                <div
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                  }}
                >
                  Address History
                </div>
              }
              {...a11yProps(5)}
            />
            <AntTab
              data-cy={"Person-alias-history-tab"}
              id={"Person-alias-history-tab"}
              style={{
                borderTopLeftRadius: "10px",
                borderTopRightRadius: "10px",
                border: "1px solid rgb(48, 62, 79)",
                borderBottom: "none",
              }}
              label={
                <div
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                  }}
                >
                  Alias History
                </div>
              }
              {...a11yProps(6)}
            />
            <AntTab
              data-cy={"Person-known-associate-tab"}
              id={"Person-known-associate-tab"}
              style={{
                borderTopLeftRadius: "10px",
                borderTopRightRadius: "10px",
                border: "1px solid rgb(48, 62, 79)",
                borderBottom: "none",
              }}
              label={
                <div
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                  }}
                >
                  {knownAssociatesHeader()}
                </div>
              }
              {...a11yProps(7)}
            />
            <AntTab
              data-cy="Person-ticket-history-tab"
              id="Person-ticket-history-tab"
              style={{
                borderTopLeftRadius: "10px",
                borderTopRightRadius: "10px",
                border: "1px solid rgb(48, 62, 79)",
                borderBottom: "none",
              }}
              label="Ticket History"
              {...a11yProps(8)}
            />
          </AntTabs>
        </AppBar>
        <TabPanel
          id="person-incident-history-tab-panel"
          value={value}
          index={0}
        >
          <PaginatedTableWrapper
            dashSize={dashSize}
            dashWidthPercentage={0.75}
            tableHeight={dashSize?.height}
            tableName={"Incident History"}
            headers={personIncidentHistoryHeaders}
            userId={user?.id}
            paginatedQueryHandler={() => console.log("Not paginated")}
            rows={
              incidentHistoryData
                ? parsePersonIncidentHistory(
                    incidentHistoryData[Object.keys(incidentHistoryData)[0]],
                    handleIncidentOpening,
                    user?.roleNames
                  )
                : []
            }
            loading={incidentHistoryLoading}
            error={incidentHistoryError}
            paginated={false}
            resizable={false}
          />
        </TabPanel>
        <TabPanel value={value} index={1}>
          <PaginatedTableWrapper
            dashSize={dashSize}
            dashWidthPercentage={0.75}
            tableHeight={dashSize?.height}
            tableName={"Drug History"}
            headers={personDrugHistoryHeaders}
            userId={user?.id}
            paginatedQueryHandler={() => console.log("Not paginated")}
            rows={
              drugHistoryData
                ? parsePersonDrugHistory(
                    drugHistoryData[Object.keys(drugHistoryData)[0]],
                    handleIncidentOpening,
                    user?.roleNames
                  )
                : []
            }
            loading={drugHistoryLoading}
            error={null}
            paginated={false}
            resizable={false}
          />
        </TabPanel>
        <TabPanel value={value} index={2}>
          <PaginatedTableWrapper
            dashSize={dashSize}
            dashWidthPercentage={0.75}
            tableHeight={dashSize?.height}
            tableName={"Vehicle History"}
            headers={personVehicleHistoryHeaders}
            userId={user?.id}
            paginatedQueryHandler={() => console.log("Not paginated")}
            rows={
              vehicleHistoryData
                ? parsePersonVehicleHistory(
                    vehicleHistoryData[Object.keys(vehicleHistoryData)[0]]
                  )
                : []
            }
            loading={vehicleHistoryLoading}
            error={null}
            paginated={false}
            resizable={false}
          />
        </TabPanel>
        <TabPanel value={value} index={3}>
          <PaginatedTableWrapper
            dashSize={dashSize}
            dashWidthPercentage={0.75}
            tableHeight={dashSize?.height}
            tableName={"Name History"}
            headers={personNameHistoryHeaders}
            userId={user?.id}
            paginatedQueryHandler={() => console.log("Not paginated")}
            rows={
              nameHistoryData
                ? parsePersonNameHistory(
                    nameHistoryData[Object.keys(nameHistoryData)[0]]
                  )
                : []
            }
            loading={nameHistoryLoading}
            error={null}
            paginated={false}
            resizable={false}
          />
        </TabPanel>
        <TabPanel value={value} index={4}>
          <PaginatedTableWrapper
            dashSize={dashSize}
            dashWidthPercentage={0.75}
            tableHeight={dashSize?.height}
            tableName={"Phone History"}
            headers={personPhoneHistoryHeaders}
            userId={user?.id}
            paginatedQueryHandler={() => console.log("Not paginated")}
            rows={
              phoneHistoryData
                ? parsePersonPhoneHistory(
                    phoneHistoryData[Object.keys(phoneHistoryData)[0]]
                  )
                : []
            }
            loading={phoneHistoryLoading}
            error={null}
            paginated={false}
            resizable={false}
          />
        </TabPanel>
        <TabPanel value={value} index={5}>
          <PaginatedTableWrapper
            dashSize={dashSize}
            dashWidthPercentage={0.75}
            tableHeight={dashSize?.height}
            tableName={"Address History"}
            headers={personAddressHistoryHeaders}
            userId={user?.id}
            paginatedQueryHandler={() => console.log("Not paginated")}
            rows={
              addressHistoryData
                ? parsePersonAddressHistory(
                    addressHistoryData[Object.keys(addressHistoryData)[0]]
                  )
                : []
            }
            loading={addressHistoryLoading}
            error={null}
            paginated={false}
            resizable={false}
          />
        </TabPanel>
        <TabPanel value={value} index={6}>
          <PaginatedTableWrapper
            dashSize={dashSize}
            dashWidthPercentage={0.75}
            tableHeight={dashSize?.height}
            tableName={"Alias History"}
            headers={aliasHistoryHeaders}
            userId={user?.id}
            paginatedQueryHandler={() => console.log("Not paginated")}
            rows={
              aliasHistoryData
                ? parsePersonAliasHistory(
                    aliasHistoryData[Object.keys(aliasHistoryData)[0]]
                      ?.aliases || [],
                    handlePersonOpening,
                    handleAddPersonToIncident,
                    incidentId
                  )
                : []
            }
            loading={aliasHistoryLoading}
            error={aliasHistoryError}
            paginated={false}
            resizable={false}
          />
        </TabPanel>
        <TabPanel value={value} index={7}>
          <PaginatedTableWrapper
            dashSize={dashSize}
            dashWidthPercentage={0.75}
            tableHeight={dashSize?.height}
            tableName={"Known Associates"}
            headers={knownAssociatesHeaders(incidentId)}
            userId={user?.id}
            paginatedQueryHandler={() => console.log("Not paginated")}
            rows={
              knownAssociatesData
                ? parseKnownAssociates(
                    knownAssociatesData[Object.keys(knownAssociatesData)[0]],
                    handleIncidentOpening,
                    handlePersonOpening,
                    user?.roleNames,
                    handleAddPersonToIncident,
                    incidentId
                  )
                : []
            }
            loading={knownAssociatesLoading}
            error={knownAssociatesError}
            paginated={false}
            resizable={false}
          />
        </TabPanel>
        <TabPanel value={value} index={8}>
          <PaginatedTableWrapper
            dashSize={dashSize}
            dashWidthPercentage={0.75}
            tableHeight={dashSize?.height}
            tableName="Ticket History"
            headers={personTicketHistoryHeaders}
            userId={user?.id}
            rows={
              ticketHistoryData
                ? parsePersonTicketHistory(
                    ticketHistoryData.personTicketHistory,
                    handleIncidentOpening,
                    user?.roleNames,
                    handleTicketOpening
                  )
                : []
            }
            loading={ticketHistoryLoading}
            error={ticketHistoryError}
            paginated={false}
            resizable={false}
          />
        </TabPanel>
      </div>
    </Modal>
  );
};

export { TabPanel, a11yProps };
export default PersonHistoryModal;
